import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Copy, AlertTriangle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ColorField, ImageUpload, SwitchRow } from "@/components/editor/fields";
import { buildSvg, buildPng, download, toDataUrl } from "@/lib/qr";
import { fileUrl, publicCardUrl, qrTargetUrl, copyText, api } from "@/lib/api";
import { contrastRatio } from "@/lib/cardUtils";

const DEFAULTS = { fg: "#111111", bg: "#FFFFFF", margin: 4, ecl: "Q", logoSource: "none", customLogo: "", logoScale: 0.22, trackQr: true };

const Seg = ({ value, options, onChange, testId }) => (
  <div className="grid gap-1 rounded-lg border bg-white p-1" style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}>
    {options.map(([k, l]) => (
      <button key={k} type="button" data-testid={`${testId}-${k}`} onClick={() => onChange(k)} className={`rounded-md px-2 py-1.5 text-xs ${value === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{l}</button>
    ))}
  </div>
);

export const QrStudio = ({ card }) => (
  <QrDesigner
    initial={{ logoSource: card.identity.logo_url ? "logo" : "none", ...(card.qr || {}) }}
    logos={{ logo: card.identity.logo_url, avatar: card.identity.avatar_url }}
    getTarget={(o) => (o.trackQr ? qrTargetUrl(card.slug) : publicCardUrl(card.slug))}
    shareUrl={publicCardUrl(card.slug)} fileBase={`qr-${card.slug}`} saveUrl={`/cards/${card.id}/qr`} isCard
  />
);

export const QrDesigner = ({ initial, logos = {}, getTarget, shareUrl, fileBase, saveUrl, isCard = false, note }) => {
  const [o, setO] = useState(() => ({ ...DEFAULTS, ...(initial || {}) }));
  const [svg, setSvg] = useState("");
  const [logoData, setLogoData] = useState("");
  const [saved, setSaved] = useState(true);
  const savedRef = useRef(JSON.stringify({ ...DEFAULTS, ...(initial || {}) }));
  const set = (k, v) => setO((p) => ({ ...p, [k]: v }));
  const target = getTarget(o);
  const logoUrl = { logo: logos.logo, avatar: logos.avatar, custom: o.customLogo }[o.logoSource] || "";

  useEffect(() => {
    const json = JSON.stringify(o);
    if (json === savedRef.current) return;
    setSaved(false);
    const h = setTimeout(() => api.put(saveUrl, o).then(() => { savedRef.current = json; setSaved(true); }).catch(() => toast.error("Impostazioni QR non salvate")), 600);
    return () => clearTimeout(h);
  }, [o, saveUrl]);
  useEffect(() => {
    if (!logoUrl) return setLogoData("");
    toDataUrl(fileUrl(logoUrl)).then(setLogoData).catch(() => setLogoData(""));
  }, [logoUrl]);
  const opts = { ...o, logo: logoData };
  useEffect(() => {
    buildSvg(target, opts).then(setSvg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, o.fg, o.bg, o.margin, o.ecl, o.logoScale, logoData]);

  const { ratio, darkOnLight } = contrastRatio(o.fg, o.bg);
  const fname = fileBase;
  const dlPng = async () => {
    download(await buildPng(target, opts, 2048), `${fname}.png`);
    toast.success("PNG 2048×2048 scaricato");
  };
  const dlSvg = () => {
    download(URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" })), `${fname}.svg`);
    toast.success("SVG vettoriale scaricato");
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
      <div className="panel flex flex-col items-center justify-center gap-6 p-8 sm:p-12">
        <div className="qr-preview w-full max-w-[360px] rounded-xl border p-3 shadow-sm" style={{ background: o.bg }} data-testid="qr-preview" dangerouslySetInnerHTML={{ __html: svg }} />
        <div className="w-full max-w-[420px] text-center">
          <div className="eyebrow">URL codificato</div>
          <div className="mt-1 break-all font-mono-ui text-xs text-zinc-600" data-testid="qr-target-url">{target}</div>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={dlPng} data-testid="qr-download-png"><Download className="mr-2 h-4 w-4" />PNG alta risoluzione</Button>
          <Button variant="outline" onClick={dlSvg} data-testid="qr-download-svg"><Download className="mr-2 h-4 w-4" />SVG vettoriale</Button>
          <Button variant="ghost" data-testid="qr-copy-url" onClick={async () => { await copyText(shareUrl); toast.success("URL copiato"); }}><Copy className="mr-2 h-4 w-4" />Copia URL</Button>
          <Button variant="ghost" onClick={() => window.open(shareUrl, "_blank")} data-testid="qr-open-public"><ExternalLink className="mr-2 h-4 w-4" />Apri</Button>
        </div>
      </div>
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="eyebrow">Colori</div>
          <div className="grid grid-cols-2 gap-2">
            <ColorField label="Primo piano" value={o.fg} onChange={(v) => set("fg", v)} testId="qr-fg-color" />
            <ColorField label="Sfondo" value={o.bg} onChange={(v) => set("bg", v)} testId="qr-bg-color" />
          </div>
          {(ratio < 4 || !darkOnLight) && (
            <p className="flex gap-1.5 text-[11px] text-amber-700" data-testid="qr-contrast-warning"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {!darkOnLight ? "QR invertito (chiaro su scuro): alcuni lettori potrebbero non riconoscerlo." : `Contrasto basso (${ratio.toFixed(1)}:1). Consigliato almeno 4:1 per la stampa.`}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between"><span className="eyebrow">Logo al centro</span><span className="text-[11px] text-zinc-400" data-testid="qr-save-state">{saved ? "Salvato" : "Salvataggio…"}</span></div>
          <Seg testId="qr-logo" value={o.logoSource} onChange={(v) => set("logoSource", v)} options={isCard ? [["none", "Nessuno"], ["logo", "Logo card"], ["avatar", "Foto"], ["custom", "Altro logo"]] : [["none", "Nessuno"], ["custom", "Carica logo"]]} />
          {(o.logoSource === "custom" || (o.logoSource === "logo" && !logos.logo)) && (
            <div className="space-y-1.5">
              {o.logoSource === "logo" && <p className="text-[11px] text-amber-700">Questa card non ha ancora un logo: caricalo qui per usarlo nel QR (oppure aggiungilo nell'editor, sezione Identità).</p>}
              <ImageUpload label="Logo del cliente per il QR (PNG trasparente o quadrato)" value={o.customLogo} onChange={(v) => setO((p) => ({ ...p, customLogo: v, logoSource: v ? "custom" : "none" }))} testId="qr-custom-logo" />
            </div>
          )}
          {o.logoSource === "avatar" && !logoUrl && <p className="text-[11px] text-amber-700">Nessuna foto profilo in questa card.</p>}
          {logoUrl && (
            <div>
              <div className="flex justify-between text-[11px] text-zinc-500"><span>Dimensione logo</span><span>{Math.round(o.logoScale * 100)}%</span></div>
              <Slider min={0.14} max={0.26} step={0.01} value={[o.logoScale]} onValueChange={([v]) => set("logoScale", v)} data-testid="qr-logo-size" />
              <p className="mt-1.5 text-[11px] text-zinc-400">Con il logo la correzione errori è impostata automaticamente al livello massimo (H, 30%).</p>
            </div>
          )}
        </div>
        {!logoUrl && (
          <div className="space-y-2">
            <div className="eyebrow">Correzione errori</div>
            <Seg testId="qr-ecl" value={o.ecl} onChange={(v) => set("ecl", v)} options={[["M", "M · 15%"], ["Q", "Q · 25%"], ["H", "H · 30%"]]} />
          </div>
        )}
        <div className="space-y-2">
          <div className="flex justify-between"><span className="eyebrow">Quiet zone</span><span className="font-mono-ui text-xs text-zinc-500">{o.margin} moduli</span></div>
          <Slider min={2} max={8} step={1} value={[o.margin]} onValueChange={([v]) => set("margin", v)} data-testid="qr-margin" />
          <p className="text-[11px] text-zinc-400">Lo standard consiglia 4 moduli di margine bianco attorno al codice.</p>
        </div>
        {isCard && <SwitchRow label="Distingui le scansioni QR" hint="Aggiunge ?s=qr all'URL per le statistiche" checked={o.trackQr} onChange={(v) => set("trackQr", v)} testId="qr-track-toggle" />}
        <p className="text-[11px] leading-relaxed text-zinc-500">{note || "Il QR contiene solo l'indirizzo permanente della card: puoi modificare testi, foto, link e colori senza ristamparlo."} Per la stampa usa l'SVG o il PNG a 2048 px con un lato minimo di 2 cm.</p>
      </div>
    </div>
  );
};
